import type Highcharts from 'highcharts';
import type { AnalysisRanges } from './analysisTypes';
import type { DirectShearReading } from '../../../types/directShearReading';

/** The full peak or residual row from readings, used to place dots on all 4 plots. */
export type MarkerRow = DirectShearReading | null;

// Stage-4 was '#FFC000' (yellow) — identical to the peak-highlight yellow
// (CHART_COLORS.yellow), so a stage-4 line was indistinguishable from a peak
// band. Changed to purple per the 2026-06-12 meeting. Keep in sync with the
// backend report copy in backend/src/report/chartScripts.ts.
export const STAGE_COLORS = ['#4472C4', '#ED7D31', '#70AD47', '#7030A0', '#7F7F7F'] as const;
export const STAGE_MARKERS = ['square', 'triangle', 'triangle-down', 'diamond', 'circle'] as const;

export const CHART_COLORS = {
  blue: '#4472C4',
  green: '#70AD47',
  yellow: '#FFC000',
  red: '#FF0000',
} as const;

export const baseChartOptions: Highcharts.Options = {
  chart: {
    type: 'spline',
    height: 450,
    zooming: {
      type: 'x',
      mouseWheel: { enabled: true },
      resetButton: { theme: { style: { display: 'none' } } },
    },
    panning: { enabled: true, type: 'x' },
    panKey: 'shift',
    borderWidth: 1,
    borderColor: '#e0e0d8',
    borderRadius: 8,
    spacing: [20, 20, 15, 15],
  },
  credits: { enabled: false },
  xAxis: {
    title: { text: 'Horizontal Displacement (mm)' },
    gridLineWidth: 1,
    min: 0,
    crosshair: { width: 1, color: '#99999944', dashStyle: 'Dot', snap: true },
  },
  yAxis: {
    tickAmount: 9,
    gridLineWidth: 1,
  },
  tooltip: {
    enabled: true,
    shared: true,
    snap: 8,
    hideDelay: 0,
    style: { fontSize: '11px' },
    headerFormat: '<span style="font-size:10px;color:#666">{point.key:.3f} mm</span><br/>',
    pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.2f}</b>',
    padding: 5,
    shadow: false,
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: 'rgba(255,255,255,0.95)',
    followPointer: false,
    positioner: function (this: Highcharts.Tooltip, labelWidth: number, _labelHeight: number, point: Highcharts.TooltipPositionerPointObject) {
      const chart = this.chart;
      const plotW = chart.plotWidth;
      const plotLeft = chart.plotLeft;
      // Snap to opposite corner: cursor in left half → top-right, cursor in right half → top-left
      const cursorInLeftHalf = (point.plotX ?? 0) < plotW / 2;
      const x = cursorInLeftHalf
        ? plotLeft + plotW - labelWidth - 8
        : plotLeft + 8;
      // BUG-2026-0199 #5: the synced "Reset zoom" button (useSyncedCharts) is
      // rendered at the plot's top-right — the same slot this callout uses when
      // the cursor is in the left half. When that button is present, drop the
      // top-right callout below it so the hovered value is never hidden behind
      // the button.
      const resetBtn = (chart as unknown as { _syncResetBtn?: Highcharts.SVGElement })._syncResetBtn;
      const clearsButton = cursorInLeftHalf && !!resetBtn;
      const btnClearance = clearsButton ? ((resetBtn!.getBBox?.().height ?? 24) + 10) : 0;
      return { x, y: chart.plotTop + 8 + btnClearance };
    },
  },
  plotOptions: {
    spline: {
      marker: { enabled: false },
      lineWidth: 2.5,
      turboThreshold: 0,
      states: {
        hover: { lineWidthPlus: 0 },
        inactive: { opacity: 1 },
      },
      stickyTracking: false,
    },
    scatter: {
      turboThreshold: 0,
      stickyTracking: false,
    },
    series: {
      animation: { duration: 600 },
      states: { inactive: { opacity: 1 } },
      stickyTracking: false,
    },
  },
  exporting: {
    enabled: true,
    buttons: {
      contextButton: {
        menuItems: [
          'viewFullscreen',
          'separator',
          'downloadPNG',
          'downloadSVG',
          'downloadPDF',
          'separator',
          'viewData',
        ],
      },
    },
  },
};

/**
 * Build Highcharts zones for color-coding peak (yellow) and residual (red)
 * ranges on Plots 1 & 2. Outside ranges = green (default series color).
 */
export function buildColorZones(
  ranges: AnalysisRanges,
): Highcharts.SeriesZonesOptionsObject[] | undefined {
  const hasPeak = ranges.peakRangeFrom != null && ranges.peakRangeTo != null;
  const hasResidual = ranges.residualRangeFrom != null && ranges.residualRangeTo != null;

  if (!hasPeak && !hasResidual) return undefined;

  const zones: Highcharts.SeriesZonesOptionsObject[] = [];

  if (hasPeak && hasResidual) {
    // Determine which range comes first on the X axis
    const peakFirst = ranges.peakRangeFrom! <= ranges.residualRangeFrom!;
    const firstRange = peakFirst
      ? { from: ranges.peakRangeFrom!, to: ranges.peakRangeTo!, color: CHART_COLORS.yellow }
      : { from: ranges.residualRangeFrom!, to: ranges.residualRangeTo!, color: CHART_COLORS.red };
    const secondRange = peakFirst
      ? { from: ranges.residualRangeFrom!, to: ranges.residualRangeTo!, color: CHART_COLORS.red }
      : { from: ranges.peakRangeFrom!, to: ranges.peakRangeTo!, color: CHART_COLORS.yellow };

    zones.push(
      { value: firstRange.from, color: CHART_COLORS.green },
      { value: firstRange.to, color: firstRange.color },
      { value: secondRange.from, color: CHART_COLORS.green },
      { value: secondRange.to, color: secondRange.color },
      { color: CHART_COLORS.green },
    );
  } else if (hasPeak) {
    zones.push(
      { value: ranges.peakRangeFrom!, color: CHART_COLORS.green },
      { value: ranges.peakRangeTo!, color: CHART_COLORS.yellow },
      { color: CHART_COLORS.green },
    );
  } else {
    zones.push(
      { value: ranges.residualRangeFrom!, color: CHART_COLORS.green },
      { value: ranges.residualRangeTo!, color: CHART_COLORS.red },
      { color: CHART_COLORS.green },
    );
  }

  return zones;
}

/**
 * Build Highcharts plotBands for peak (light yellow) and residual (light red)
 * background bands on Plots 3 & 4.
 */
export function buildPlotBands(
  ranges: AnalysisRanges,
): Highcharts.XAxisPlotBandsOptions[] {
  const bands: Highcharts.XAxisPlotBandsOptions[] = [];

  if (ranges.peakRangeFrom != null && ranges.peakRangeTo != null) {
    bands.push({
      from: ranges.peakRangeFrom,
      to: ranges.peakRangeTo,
      color: '#FFC00033',
      label: { text: 'Peak', style: { fontSize: '10px', color: '#B8860B' } },
    });
  }

  if (ranges.residualRangeFrom != null && ranges.residualRangeTo != null) {
    bands.push({
      from: ranges.residualRangeFrom,
      to: ranges.residualRangeTo,
      color: '#FF000033',
      label: { text: 'Residual', style: { fontSize: '10px', color: '#CC0000' } },
    });
  }

  return bands;
}

/**
 * Build peak & residual scatter series for any chart.
 * Pass the Y field name to extract the correct value from the marker row.
 */
export function buildMarkerSeries(
  peakRow: MarkerRow,
  _residualRow: MarkerRow,
  yField: keyof DirectShearReading,
): Highcharts.SeriesOptionsType[] {
  const series: Highcharts.SeriesOptionsType[] = [];

  if (peakRow && peakRow.HorizontalDisplacementCumulative != null && peakRow[yField] != null) {
    series.push({
      type: 'scatter',
      name: 'Peak',
      data: [{ x: peakRow.HorizontalDisplacementCumulative, y: peakRow[yField] as number }],
      marker: {
        symbol: 'circle',
        radius: 4,
        fillColor: CHART_COLORS.yellow,
        lineColor: '#000000',
        lineWidth: 2,
      },
      tooltip: {
        headerFormat: '<b>Peak</b><br/>',
        pointFormat: 'X: {point.x:.4f} mm<br/>Y: {point.y:.4f}',
      },
      zIndex: 10,
      showInLegend: true,
    });
  }

  // Residual is a range, not a single point — no dot marker

  return series;
}
